/**
 * ADR-0161 §7.3 — client-side presentation of the three code-deactivation
 * guards.
 *
 * The SERVER is the enforcement; nothing here decides anything. What this
 * module does is (a) turn the hub's refusal codes into row-level sentences an
 * admin can act on, and (b) let the page disable a Deactivate button the client
 * can already see will be refused — so the common cases do not require a round
 * trip to learn.
 *
 * The predicate is deliberately CONSERVATIVE: it returns a reason only when the
 * client is certain. In-use-on-an-open-item is not knowable from the codes list
 * alone, so that case is never pre-disabled and always arrives as a refusal.
 * A button disabled on a guess is worse than one that refuses with a sentence.
 *
 * Lives in `lib/` because the console test glob is `*.test.ts` — there is no
 * React test renderer, so logic that needs testing is extracted here rather
 * than left in the route (a standing repo constraint).
 */

/** The reserved auto-resolve codes. Mirrors the hub's RESERVED_ACTION_ITEM_CODES. */
export const RESERVED_ACTION_ITEM_CODES = ["auto_resolve_signal_cleared", "auto_resolve_no_investigation"] as const;

export type CodeRow = { id: string; code: string; label: string; active: boolean };

export type DeactivationBlock = "reserved" | "last_active";

/**
 * Why the client can already tell this deactivation will be refused, or null
 * when it cannot tell (which includes the in-use case — see the header).
 *
 * `codes` is the full list for this kind, INCLUDING inactive rows; the floor
 * counts active ones.
 */
export function deactivationBlockedReason(row: CodeRow, codes: readonly CodeRow[]): DeactivationBlock | null {
  if (!row.active) return null; // already inactive — nothing to block
  if ((RESERVED_ACTION_ITEM_CODES as readonly string[]).includes(row.code)) return "reserved";
  const activeCount = codes.filter((c) => c.active).length;
  if (activeCount <= 1) return "last_active";
  return null;
}

/**
 * The sentence shown when the client blocks the gesture up front. Same content
 * as the server's refusal, phrased for someone who has not yet clicked.
 */
export function deactivationBlockedMessage(reason: DeactivationBlock, kindLabel: string): string {
  if (reason === "reserved") {
    return "This code is reserved for watch auto-resolution and can't be deactivated. To stop watches from auto-resolving, change the watch's response configuration.";
  }
  return `This is the last active ${kindLabel.toLowerCase()} code. Add another code before deactivating this one.`;
}

/**
 * Map a hub refusal onto the row-level error sentence.
 *
 * Returns null for codes this function does not own, so the caller falls back
 * to the server's own message rather than swallowing it — a friendly-but-vague
 * error is worse than the raw one.
 */
export function deactivationRefusalMessage(errorCode: string | undefined, kindLabel: string): string | null {
  switch (errorCode) {
    case "code_in_use":
      return "This code is in use on an open action item. Close those items or reassign them first.";
    case "reserved_code":
      return deactivationBlockedMessage("reserved", kindLabel);
    case "last_active_code":
      return deactivationBlockedMessage("last_active", kindLabel);
    default:
      return null;
  }
}
